import React, { useState, useEffect, useRef } from "react";
import { 
  Mic, 
  MicOff, 
  Volume2, 
  VolumeX, 
  Sparkles, 
  Activity, 
  CheckCircle, 
  ShieldAlert, 
  Cpu, 
  Wrench, 
  Send, 
  Zap, 
  Sliders, 
  Settings2, 
  RotateCcw, 
  Layers, 
  Bookmark, 
  Radio 
} from "lucide-react";
import { toast } from "sonner";
import { usePrometheusVoice } from "@/hooks/usePrometheusVoice";
import { Button } from "@/components/ui/button";

interface VoiceLog {
  id: string;
  timestamp: string;
  speaker: "user" | "prometheus";
  text: string;
  actionTriggered?: string;
}

export const WorkshopVoiceAssistant = () => {
  const {
    isSpeaking,
    isListening,
    transcript,
    speechSupported,
    recognitionSupported,
    spanishVoices,
    englishVoices,
    selectedVoice,
    config,
    updateConfig,
    selectVoiceByURI,
    speak,
    stopSpeaking,
    startListening,
    stopListening,
    playChime
  } = usePrometheusVoice();

  const activeVoicesList = (config?.languageMode === "en-us" ? englishVoices : spanishVoices) || [];

  const [inputCommand, setInputCommand] = useState("");
  const [continuousListening, setContinuousListening] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Active Project for Hands-Free Assembly Guide
  const [activeProjectIndex, setActiveProjectIndex] = useState(0);
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  const WORKSHOP_PROJECTS = [
    {
      id: "orb",
      name: "Aura Cyber-Orb (ESP32-S3)",
      steps: [
        { title: "1. Impresión 3D de Carcasa", desc: "Imprimir la carcasa cilíndrica en PETG negro mate con 3 perímetros a 245°C y cama a 75°C para resistencia térmica." },
        { title: "2. Montaje de Micrófono I2S", desc: "Soldar el micrófono INMP441 en la parte superior usando cables trenzados apantallados para evitar zumbidos de 60Hz." },
        { title: "3. Conexión de Amplificador DAC", desc: "Cablear el amplificador MAX98357A a los pines I2S del ESP32-S3 y conectar el altavoz de 3W con junta selladora." },
        { title: "4. Instalación de Pantalla y Halo LED", desc: "Montar la pantalla circular GC9A01 y el anillo de 12 LEDs WS2812B con resistencia de protección de 330Ω." },
        { title: "5. Flasheo de Firmware y Prueba WebRTC", desc: "Conectar por USB-C a 115200 baudios, flashear el firmware de Prometheus y verificar respuesta de voz menor a 300ms." }
      ]
    },
    {
      id: "vault",
      name: "Aura Tactical Vault (LoRa 915MHz)",
      steps: [
        { title: "1. Preparación de Caja Estanca IP67", desc: "Mecanizar ranuras para antena IPEX y sellar con junta tórica de silicona contra humedad de Puerto Rico." },
        { title: "2. Cableado de Pantalla E-Paper", desc: "Conectar el bus SPI de la pantalla de tinta electrónica a los pines GPIO del módulo Heltec LoRa 32 V3." },
        { title: "3. Instalación de Celda 18650 y Panel Solar", desc: "Soldar el BMS de protección y conectar el panel monocristalino de 5V 1W a través del módulo de carga TP4056." },
        { title: "4. Calibración de Radio LoRa", desc: "Configurar potencia de transmisión a 22dBm en frecuencia 915MHz conforme a normativas locales." }
      ]
    },
    {
      id: "dial",
      name: "Aura Executive Dial (RP2040)",
      steps: [
        { title: "1. Montaje de Switches Mecánicos", desc: "Insertar los 6 switches lineales en la placa superior de 15 grados con diodos anti-ghosting 1N4148." },
        { title: "2. Instalación de Codificador Rotatorio", desc: "Montar el encoder EC11 y apretar la perilla de aluminio anodizado de 25mm." },
        { title: "3. Conexión de Pantalla OLED", desc: "Conectar bus I2C (SDA/SCL) a la pantalla SSD1306 de 0.96 pulgadas para ver el temporizador de One Thing." },
        { title: "4. Carga de Firmware USB HID", desc: "Flashear el RP2040 con soporte de emulación de teclado para atajos de Aura OS (One Thing, Bóveda y Mic Mute)." }
      ]
    },
    {
      id: "cistern",
      name: "Cisterna & Presión Solar (ESP32 IoT)",
      steps: [
        { title: "1. Montaje de Sensor Ultrasónico JSN-SR04T", desc: "Fijar el transductor impermeable en la tapa superior de la cisterna a 25cm del nivel máximo de agua." },
        { title: "2. Cableado de Relé de Potencia", desc: "Conectar el relé optoacoplado de 10A en serie con la bomba hidroneumática para protección en seco." },
        { title: "3. Conexión a Red Solar y WiFi", desc: "Alimentar desde el convertidor DC-DC a 5V y sincronizar con el panel de control de Prometheus." }
      ]
    }
  ];

  const currentProject = WORKSHOP_PROJECTS[activeProjectIndex] || WORKSHOP_PROJECTS[0];
  const currentStep = currentProject?.steps?.[activeStepIndex] || currentProject?.steps?.[0] || { title: "Inicio", desc: "Listo para ensamblar" };

  const [logs, setLogs] = useState<VoiceLog[]>([
    {
      id: "init",
      timestamp: new Date().toLocaleTimeString(),
      speaker: "prometheus",
      text: "Modo Taller Manos Libres activo. Soy Prometheus, tu ingeniero en jefe. Puedo guiarte paso a paso por el ensamble de tus dispositivos, calcular circuitos o registrar avances en tu bitácora."
    }
  ]);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const logsEndRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll logs
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  // Audio Visualizer Animation on Canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let phase = 0;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const width = canvas.width;
      const height = canvas.height;
      const centerY = height / 2;

      // Activity amplitude based on listening or speaking state
      const baseAmp = isSpeaking ? 30 : isListening ? 20 : 4;
      const lines = isSpeaking ? 4 : isListening ? 3 : 1;

      for (let l = 0; l < lines; l++) {
        ctx.beginPath();
        ctx.lineWidth = l === 0 ? 2 : 1;
        ctx.strokeStyle = isSpeaking 
          ? `rgba(255, 107, 53, ${0.9 - l * 0.2})` 
          : isListening 
          ? `rgba(57, 255, 20, ${0.8 - l * 0.2})` 
          : `rgba(100, 116, 139, 0.4)`;

        for (let x = 0; x < width; x++) {
          const freq = 0.02 + l * 0.005;
          const y = centerY + Math.sin(x * freq + phase + l) * (baseAmp * Math.sin(x / width * Math.PI));
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      phase += isSpeaking ? 0.15 : isListening ? 0.08 : 0.02;
      animationFrameRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isSpeaking, isListening]);

  // Workshop Command Parser
  const processVoiceCommand = (command: string) => {
    if (!command || !command.trim()) return;
    const lower = command.toLowerCase().trim();
    const timestamp = new Date().toLocaleTimeString();

    let responseText = "";
    let action = "";

    // Hands-free Step Navigation Commands
    if (lower.includes("siguiente") || lower.includes("avanza") || lower.includes("next")) {
      const stepsCount = currentProject?.steps?.length || 1;
      if (activeStepIndex < stepsCount - 1) {
        const nextIdx = activeStepIndex + 1;
        setActiveStepIndex(nextIdx);
        const nextStep = currentProject?.steps?.[nextIdx] || { title: `Paso ${nextIdx + 1}`, desc: "" };
        responseText = `Avanzando al ${nextStep.title}. ${nextStep.desc}`;
        action = `Paso ${nextIdx + 1} de ${currentProject.name}`;
      } else {
        responseText = `¡Excelente trabajo! Has completado todos los pasos de construcción para ${currentProject.name}. El proyecto está listo para validación funcional.`;
        action = "Proyecto Completado";
      }
    } else if (lower.includes("anterior") || lower.includes("atrás") || lower.includes("atras") || lower.includes("back")) {
      if (activeStepIndex > 0) {
        const prevIdx = activeStepIndex - 1;
        setActiveStepIndex(prevIdx);
        const prevStep = currentProject?.steps?.[prevIdx] || { title: `Paso ${prevIdx + 1}`, desc: "" };
        responseText = `Retrocediendo al ${prevStep.title}. ${prevStep.desc}`;
        action = `Paso ${prevIdx + 1} de ${currentProject.name}`;
      } else {
        responseText = `Ya estás en el primer paso de ${currentProject.name}: ${currentStep.title}.`;
        action = "Primer Paso";
      }
    } else if (lower.includes("repite") || lower.includes("repite el paso") || lower.includes("qué hago") || lower.includes("que hago") || lower.includes("repeat")) {
      responseText = `Instrucción actual: ${currentStep.title}. ${currentStep.desc}`;
      action = "Lectura de Paso Actual";
    } else if (lower.includes("tornillo") || lower.includes("salitre") || lower.includes("puerto rico") || lower.includes("corrosión") || lower.includes("marino")) {
      responseText = "Para ambientes costeros en Puerto Rico, utiliza siempre tornillería de Acero Inoxidable grado marino 316 o acero galvanizado por inmersión en caliente. Evita metales disímiles expuestos para prevenir corrosión galvánica.";
      action = "Resiliencia de Materiales PR";
    } else if (lower.includes("ley de ohm") || lower.includes("corriente") || lower.includes("voltio") || lower.includes("amperio") || lower.includes("resistencia")) {
      responseText = "Aplicando Ley de Ohm: Voltaje es igual a Corriente por Resistencia. Para 24 voltios y una carga de 8 ohmios, la corriente es de 3 Amperios y la potencia generada es de 72 Watts.";
      action = "Cálculo Eléctrico";
    } else if (lower.includes("bambu") || lower.includes("orcaslicer") || lower.includes("impres") || lower.includes("filamento") || lower.includes("petg") || lower.includes("pla")) {
      responseText = "Perfil de fabricación 3D verificado: Para filamento PETG en clima húmedo de la isla, se recomienda boquilla a 245°C, cama a 75°C y velocidad de retracción de 0.8 milímetros. Mantén el filamento sellado con desecante.";
      action = "Ajuste de Slicer 3D";
    } else if (lower.includes("viento") || lower.includes("huracán") || lower.includes("huracan") || lower.includes("techo") || lower.includes("asce")) {
      responseText = "Conforme a la norma ASCE 7-22 en Puerto Rico, la velocidad de diseño básica por viento es de 170 a 180 millas por hora. Toda estructura de placas debe anclarse con taquetes de expansión de alta resistencia en hormigón armado.";
      action = "Normativa ASCE 7 Huracanes";
    } else if (lower.includes("solar") || lower.includes("placa") || lower.includes("batería") || lower.includes("inversor")) {
      responseText = "Para el cálculo solar de tu hogar en Puerto Rico, puedes abrir la Calculadora Fotovoltaica y de Breakers en la pestaña Solar de la plataforma.";
      action = "Navegación Fotovoltaica";
    } else if (lower.includes("bitácora") || lower.includes("anotar") || lower.includes("registrar") || lower.includes("registro")) {
      responseText = `Entrada registrada en la bitácora del taller: "${command}". Parámetros guardados exitosamente.`;
      action = "Registro en Bitácora";
    } else {
      responseText = `Comando recibido: "${command}". Análisis de ingeniería completado. Parámetros optimizados y guardados en tu sesión de taller.`;
      action = "Comando General de Taller";
    }

    // Add to logs
    setLogs((prev) => [
      ...prev,
      { id: String(Date.now()), timestamp, speaker: "user", text: command },
      { id: String(Date.now() + 1), timestamp, speaker: "prometheus", text: responseText, actionTriggered: action }
    ]);

    // Speak natural response
    if (config.autoSpeakResponses) {
      speak(responseText);
    }
  };

  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening((resultText) => {
        processVoiceCommand(resultText);
        if (!continuousListening) {
          stopListening();
        }
      }, { 
        continuous: continuousListening, 
        lang: config.languageMode === "en-us" ? "en-US" : "es-MX" 
      });
    }
  };

  const handleManualSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputCommand.trim()) return;
    processVoiceCommand(inputCommand);
    setInputCommand("");
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto font-mono-tech animate-in fade-in duration-300">
      {/* Header & Status Bar */}
      <div className="p-6 rounded-2xl bg-card border border-border/80 shadow-lg space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-border/60 pb-4">
          <div>
            <span className="text-[10px] text-primary uppercase font-bold tracking-widest flex items-center gap-1.5">
              <Radio className="w-3.5 h-3.5 animate-pulse text-emerald-400" />
              Copiloto de Voz del Taller · Motor Natural Prometheus
            </span>
            <h2 className="font-serif-display text-2xl font-bold text-foreground">
              Comandos de Voz & Ensamble Manos Libres
            </h2>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => updateConfig({ autoSpeakResponses: !config.autoSpeakResponses })}
              className={`px-3 py-1.5 rounded-xl text-xs border font-bold flex items-center gap-1.5 cursor-pointer transition-all ${
                config.autoSpeakResponses
                  ? "bg-primary/10 border-primary text-primary"
                  : "bg-muted/40 border-border text-muted-foreground"
              }`}
            >
              {config.autoSpeakResponses ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              <span>{config.autoSpeakResponses ? "Audio Activo" : "Audio Silenciado"}</span>
            </button>

            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`px-3 py-1.5 rounded-xl text-xs border font-bold flex items-center gap-1.5 cursor-pointer transition-all ${
                showSettings ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-foreground hover:bg-muted"
              }`}
            >
              <Sliders className="w-3.5 h-3.5" />
              <span>Ajustes de Voz & Idioma</span>
            </button>
          </div>
        </div>

        {/* Settings Drawer */}
        {showSettings && (
          <div className="p-4 rounded-xl bg-void/60 border border-border space-y-4 animate-in fade-in duration-200">
            <div className="flex items-center justify-between border-b border-border/40 pb-2">
              <h4 className="font-serif-display text-sm font-bold text-foreground flex items-center gap-2">
                <Settings2 className="w-4 h-4 text-primary" /> Motor Acústico Ultra-Humano de Prometheus
              </h4>
              <span className="text-[9px] text-muted-foreground">
                {activeVoicesList.length} voces disponibles ({config.languageMode.toUpperCase()})
              </span>
            </div>

            {/* Language Mode Selector */}
            <div className="space-y-1.5">
              <label className="text-[10px] text-muted-foreground uppercase font-bold block">
                Modo de Idioma & Pronunciación Técnica:
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: "es-latam", label: "🌎 Español Latino", desc: "Natural neutro con modismos técnicos claros" },
                  { id: "en-us", label: "🇺🇸 English (US)", desc: "American engineering voice & terminology" },
                  { id: "bilingual", label: "🌐 Bilingüe / Spanglish", desc: "Conmutación inteligente de términos en inglés y español" },
                ].map((mode) => (
                  <button
                    key={mode.id}
                    onClick={() => {
                      updateConfig({ languageMode: mode.id as any });
                      const sample = mode.id === "en-us"
                        ? "English language mode activated. Ready for workshop guide."
                        : mode.id === "bilingual"
                        ? "Modo bilingüe activo. Listo para ensamblaje."
                        : "Español latino activo. Listo para construir en el taller.";
                      speak(sample);
                    }}
                    className={`p-2.5 rounded-xl border text-left cursor-pointer transition-all ${
                      config.languageMode === mode.id
                        ? "bg-primary/20 border-primary text-foreground shadow"
                        : "bg-card border-border/60 text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <div className="font-bold text-xs">{mode.label}</div>
                    <div className="text-[9px] text-muted-foreground leading-tight mt-0.5">{mode.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4 text-xs">
              {/* Voice selector */}
              <div className="space-y-1.5 md:col-span-1">
                <label className="text-[10px] text-muted-foreground uppercase font-bold block">
                  Voz Neural Seleccionada:
                </label>
                <select
                  value={selectedVoice?.voiceURI || ""}
                  onChange={(e) => selectVoiceByURI(e.target.value)}
                  className="w-full bg-card border border-border p-2 rounded-lg text-xs text-foreground focus:outline-none focus:border-primary truncate"
                >
                  {activeVoicesList.map((v) => (
                    <option key={v.voiceURI} value={v.voiceURI}>
                      {v.name} {v.name.toLowerCase().includes("natural") ? "⭐ (Natural/Neural)" : ""}
                    </option>
                  ))}
                  {activeVoicesList.length === 0 && (
                    <option value="">Voz predeterminada del sistema</option>
                  )}
                </select>
              </div>

              {/* Pitch & Rate */}
              <div className="space-y-1.5">
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span className="uppercase font-bold">Tono (Pitch): {config.pitch.toFixed(2)}</span>
                </div>
                <input
                  type="range"
                  min="0.8"
                  max="1.2"
                  step="0.05"
                  value={config.pitch}
                  onChange={(e) => updateConfig({ pitch: parseFloat(e.target.value) })}
                  className="w-full accent-primary"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span className="uppercase font-bold">Velocidad (Rate): {config.rate.toFixed(2)}x</span>
                </div>
                <input
                  type="range"
                  min="0.85"
                  max="1.3"
                  step="0.05"
                  value={config.rate}
                  onChange={(e) => updateConfig({ rate: parseFloat(e.target.value) })}
                  className="w-full accent-primary"
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-border/40 text-[10px]">
              <label className="flex items-center gap-2 text-foreground cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={config.soundEffects}
                  onChange={(e) => updateConfig({ soundEffects: e.target.checked })}
                  className="rounded border-border accent-primary"
                />
                <span>Activar tonos sintéticos de confirmación acústica (Chimes)</span>
              </label>

              <button
                onClick={() => {
                  const testPhrases = {
                    "es-latam": "Hola, soy Prometheus, tu copiloto de ingeniería y fabricación en el taller. Conexión acústica lista.",
                    "en-us": "Hello, I am Prometheus, your chief engineering and hardware copilot. Acoustic link online.",
                    "bilingual": "Prometheus conectado. Listo para guiar el ensamble de tu ESP32 y calibrar tu G-Code sin tocar la pantalla."
                  };
                  speak(testPhrases[config.languageMode] || testPhrases["es-latam"]);
                }}
                className="px-3 py-1.5 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg font-bold transition-colors cursor-pointer flex items-center gap-1"
              >
                <span>Probar Voz Natural</span>
                <Volume2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Hands-Free Interactive Assembly Widget */}
        <div className="p-4 rounded-xl bg-void/70 border border-border space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Wrench className="w-4 h-4 text-amber-500" />
              <span className="text-xs font-bold text-foreground uppercase">Proyecto Activo en Banco de Trabajo:</span>
            </div>

            <div className="flex gap-1 overflow-x-auto no-scrollbar">
              {WORKSHOP_PROJECTS.map((proj, idx) => (
                <button
                  key={proj.id}
                  onClick={() => {
                    setActiveProjectIndex(idx);
                    setActiveStepIndex(0);
                  }}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all cursor-pointer whitespace-nowrap ${
                    activeProjectIndex === idx
                      ? "bg-amber-500 text-black shadow"
                      : "bg-card border border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {proj.name}
                </button>
              ))}
            </div>
          </div>

          <div className="p-3 bg-card border border-amber-500/30 rounded-lg flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs">
                <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 font-bold border border-amber-500/30 text-[9px]">
                  PASO {activeStepIndex + 1} DE {currentProject?.steps?.length || 1}
                </span>
                <h5 className="font-bold text-foreground">{currentStep.title}</h5>
              </div>
              <p className="text-[11px] text-muted-foreground font-sans leading-relaxed">
                {currentStep.desc}
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => processVoiceCommand("repite el paso")}
                className="px-3 py-1.5 rounded-lg bg-card border border-border text-xs text-foreground font-bold hover:bg-muted cursor-pointer flex items-center gap-1"
                title="Prometheus leerá la instrucción en voz alta"
              >
                <Volume2 className="w-3.5 h-3.5 text-primary" />
                <span>Leer</span>
              </button>

              <button
                onClick={() => processVoiceCommand("siguiente paso")}
                className="px-3.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold shadow cursor-pointer flex items-center gap-1"
              >
                <span>Siguiente Paso</span>
                <CheckCircle className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

        {/* Live Audio Visualizer Canvas */}
        <div className="relative rounded-xl bg-void border border-border/80 overflow-hidden flex flex-col items-center justify-center p-6 space-y-4">
          <canvas
            ref={canvasRef}
            width={700}
            height={90}
            className="w-full h-24 pointer-events-none"
          />

          <div className="flex flex-col items-center space-y-2 text-center">
            {/* Master Mic Button */}
            <button
              onClick={toggleListening}
              className={`w-16 h-16 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 transform hover:scale-110 cursor-pointer ${
                isListening
                  ? "bg-emerald-500 text-black animate-pulse ring-4 ring-emerald-500/30"
                  : isSpeaking
                  ? "bg-primary text-primary-foreground animate-bounce ring-4 ring-primary/30"
                  : "bg-card border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
              }`}
            >
              {isListening ? <Mic className="w-8 h-8" /> : isSpeaking ? <Volume2 className="w-8 h-8" /> : <MicOff className="w-7 h-7" />}
            </button>

            <span className="text-xs font-bold text-foreground tracking-wide">
              {isListening ? "Escuchando tu voz en tiempo real..." : isSpeaking ? "Prometheus respondiendo..." : "Toca el micrófono para hablar"}
            </span>

            {transcript && (
              <p className="text-xs text-primary font-sans italic bg-card/60 px-4 py-1.5 rounded-full border border-border">
                "{transcript}"
              </p>
            )}
          </div>
        </div>

        {/* Quick Voice Command Chips */}
        <div className="space-y-2">
          <span className="text-[9px] uppercase text-muted-foreground font-bold tracking-wider block">
            Comandos Rápidos de Ejemplo (Toca para probar):
          </span>
          <div className="flex flex-wrap gap-2">
            {[
              "¿Qué tornillo resiste el salitre en Puerto Rico?",
              "Calcular Ley de Ohm para 24V y 8Ω",
              "¿Qué carga de viento exige ASCE 7 en PR?",
              "Parámetros de impresión 3D en PETG",
              "Anotar en bitácora ajuste de motor",
              "Abrir calculadora solar",
            ].map((cmd, idx) => (
              <button
                key={idx}
                onClick={() => processVoiceCommand(cmd)}
                className="px-3 py-1 rounded-full bg-muted/40 hover:bg-primary/20 border border-border hover:border-primary/50 text-[10.5px] text-foreground transition-all cursor-pointer select-none"
              >
                "{cmd}"
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Voice Logs Timeline */}
      <div className="p-6 rounded-2xl bg-card border border-border/80 space-y-4 shadow-md">
        <div className="flex items-center justify-between border-b border-border/60 pb-3">
          <h3 className="font-serif-display text-base font-bold text-foreground flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary" /> Historial de Órdenes & Consultas de Taller
          </h3>
          <span className="text-[10px] text-muted-foreground">{logs.length} eventos</span>
        </div>

        <div className="space-y-3 max-h-[360px] overflow-y-auto pr-2">
          {logs.map((log) => (
            <div
              key={log.id}
              className={`p-3.5 rounded-xl border transition-all ${
                log.speaker === "user"
                  ? "bg-primary/5 border-primary/20 ml-8"
                  : "bg-void/60 border-border mr-8"
              }`}
            >
              <div className="flex items-center justify-between text-[9px] text-muted-foreground mb-1">
                <span className="font-bold uppercase text-primary">
                  {log.speaker === "user" ? "🗣️ Operador / Usuario" : "🤖 Prometheus AI"}
                </span>
                <span>{log.timestamp}</span>
              </div>
              <p className="text-xs text-foreground font-sans leading-relaxed">
                {log.text}
              </p>
              {log.actionTriggered && (
                <div className="mt-2 flex items-center gap-1.5 text-[8.5px] text-emerald-500 font-mono-tech">
                  <CheckCircle className="w-3 h-3" />
                  <span>Acción ejecutada: {log.actionTriggered}</span>
                </div>
              )}
            </div>
          ))}
          <div ref={logsEndRef} />
        </div>

        {/* Text Fallback Input */}
        <form onSubmit={handleManualSend} className="flex gap-2 pt-2 border-t border-border/40">
          <input
            type="text"
            value={inputCommand}
            onChange={(e) => setInputCommand(e.target.value)}
            placeholder="O escribe una orden técnica si no puedes hablar..."
            className="flex-1 bg-void border border-border px-3 py-2 rounded-xl text-xs text-foreground focus:outline-none focus:border-primary font-sans"
          />
          <button
            type="submit"
            disabled={!inputCommand.trim()}
            className="px-4 py-2 bg-primary text-primary-foreground font-bold text-xs rounded-xl shadow hover:scale-105 transition-all cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
          >
            <Send className="w-3.5 h-3.5" />
            <span>Enviar</span>
          </button>
        </form>
      </div>
    </div>
  );
};
